import React, {useEffect, useRef, useState} from 'react';
import {appText} from '../../../assets/text/appText';
import Lottie from 'lottie-react';
import {loadingLottie} from '../../../assets/lottie';
import {
  MEDIA_FILES_AI_IMAGE,
  MEDIA_FILES_AI_VIDEO,
  MEDIA_FILES_ALL,
  MEDIA_FILES_ALL_IMAGE,
  MEDIA_FILES_ALL_VIDEO,
  MEDIA_FILES_DOCUMENT,
  MEDIA_FILES_DOCUMENT_AND_VIDEO,
  MEDIA_FILES_AUDIO,
  MEDIA_FILES_IMAGE,
  MEDIA_FILES_VIDEO,
} from '../../../data/models/LibraryFile/MediaFile';
import {toast} from 'react-toastify';
import {getErrorFromCode, internalErrorCodes} from '../../../data/errorCodes';
import {toastConfig} from '../../Toast/toastConfig';
import './styles.css';

interface Props {
  onVideoSelected?: (video: File) => void;
  onFilesSelected?: (files: File[]) => void;
  multiple?: boolean;
  isLoading?: boolean;
  type?: number;
  maxFileSize?: number;
  acceptOverride?: string;
}

const UploadVideoButton = React.memo(
  ({
    onVideoSelected,
    onFilesSelected,
    multiple,
    isLoading,
    type,
    acceptOverride,
  }: Props) => {
    const [file, setFile] = useState<File>();
    const inputFile = useRef<any>(null);

    useEffect(() => {
      if (file && onVideoSelected) {
        onVideoSelected(file);
        setFile(undefined);
      }
    }, [file]);

    const onUpload = () => {
      inputFile.current.value = '';
      inputFile.current?.click();
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const picked = Array.from(e.target.files ?? []);
      if (!picked.length) {
        toast.error(getErrorFromCode(''), toastConfig);
        return;
      }

      if (multiple && onFilesSelected) {
        const supported = picked.filter(fileIsSupported);
        if (supported.length < picked.length) {
          toast.error(
            getErrorFromCode(internalErrorCodes.INVALID_FILE_EXTENSION),
            toastConfig,
          );
        }
        if (supported.length) {
          onFilesSelected(supported);
        }
        return;
      }

      const file = picked[0];

      if (fileIsAnImage(file)) {
        processImage(file);
        return;
      }

      if (fileIsAVideo(file)) {
        setFile(file);
        return;
      }

      if (fileIsADoc(file) && onVideoSelected) {
        onVideoSelected(file);
        return;
      }

      if (fileIsAudio(file) && onVideoSelected) {
        onVideoSelected(file);
        return;
      }

      toast.error(
        getErrorFromCode(internalErrorCodes.INVALID_FILE_EXTENSION),
        toastConfig,
      );
    };

    const processImage = async (file: File) => {
      if (!onVideoSelected) return;
      onVideoSelected(file);
    };

    const fileIsAnImage = (file: File) => {
      return file.type.includes('image');
    };

    const fileIsADoc = (file: File) => {
      return (
        file.type.includes('pdf') ||
        file.type.includes('msword') ||
        file.type.includes('wordprocessingml.document') ||
        file.type.includes('officedocument.presentationml')
      );
    };

    const fileIsAVideo = (file: File) => {
      return file.type.includes('video');
    };

    const fileIsAudio = (file: File) => {
      return file.type.includes('audio');
    };

    const fileIsSupported = (file: File) => {
      return (
        fileIsAnImage(file) ||
        fileIsAVideo(file) ||
        fileIsADoc(file) ||
        fileIsAudio(file)
      );
    };

    const getFileTypesByType = () => {
      switch (type) {
        case MEDIA_FILES_ALL:
          return 'image/jpg,image/jpeg,image/png,video/mp4,video/x-m4v,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation,audio/mpeg,audio/wav';
        case MEDIA_FILES_ALL_VIDEO:
          return 'image/jpg,image/jpeg,image/png,video/mp4,video/x-m4v,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation';
        case MEDIA_FILES_ALL_IMAGE:
        case MEDIA_FILES_IMAGE:
        case MEDIA_FILES_AI_IMAGE:
          return 'image/jpg,image/jpeg,image/png';
        case MEDIA_FILES_VIDEO:
        case MEDIA_FILES_AI_VIDEO:
          return 'video/mp4,video/x-m4v';
        case MEDIA_FILES_DOCUMENT:
          return 'application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation';
        case MEDIA_FILES_DOCUMENT_AND_VIDEO:
          return 'application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation,video/mp4,video/x-m4v';
        case MEDIA_FILES_AUDIO:
          return 'audio/mpeg,audio/wav';
      }
    };

    return (
      <div
        className={`UploadVideoButtonContainer ${
          isLoading ? 'UploadVideoButtonContainerDisabled' : ''
        }`}>
        <button onClick={onUpload} disabled={isLoading}>
          {isLoading ? (
            <Lottie
              animationData={loadingLottie}
              loop={true}
              style={{
                width: '14px',
                height: '14px',
                marginRight: '8.5px',
              }}
            />
          ) : (
            <span className="icon-log-out"></span>
          )}
          {appText.upload}
          <input
            type="file"
            multiple={multiple}
            onChange={onChange}
            accept={acceptOverride ?? getFileTypesByType()}
            style={{display: 'none'}}
            ref={inputFile}
          />
        </button>
      </div>
    );
  },
);

export default UploadVideoButton;
